import { useState } from 'react'
import { Link } from 'react-router-dom'
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth'
import { Eye, EyeOff, ArrowLeft, CheckCircle, Mail, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { Logo } from '../components/Logo'

type Step = 'request' | 'confirm' | 'success'

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await resetPassword({ username: email })
      toast.success('Password reset code sent to your email')
      setStep('confirm')
    } catch (error: any) {
      console.error('Reset password error:', error)
      
      if (error.name === 'UserNotFoundException') {
        toast.error('No account found with this email')
      } else if (error.name === 'LimitExceededException') {
        toast.error('Too many attempts. Please try again later')
      } else {
        toast.error(error.message || 'Failed to send reset code')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    setIsLoading(true)

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      })
      toast.success('Password reset successfully!')
      setStep('success')
    } catch (error: any) {
      console.error('Confirm reset error:', error)
      
      if (error.name === 'CodeMismatchException') {
        toast.error('Invalid verification code')
      } else if (error.name === 'ExpiredCodeException') {
        toast.error('Code has expired. Please request a new one')
      } else if (error.name === 'InvalidPasswordException') {
        toast.error('Password does not meet requirements')
      } else {
        toast.error(error.message || 'Failed to reset password')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 animate-fade-in-up">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>
          <h2 className="text-3xl font-bold text-text mb-2">
            {step === 'request' && 'Reset your password'}
            {step === 'confirm' && 'Enter reset code'}
            {step === 'success' && 'All set!'}
          </h2>
          <p className="text-text-muted">
            {step === 'request' && "Enter your email and we'll send you a reset code"}
            {step === 'confirm' && 'Check your email for the verification code'}
            {step === 'success' && 'Your password has been reset successfully'}
          </p>
        </div>

        {step === 'request' && (
          <div className="card p-8">
            <form className="space-y-6" onSubmit={handleRequestReset}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-2 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-accent" />
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full px-5 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-accent/20"
                >
                  {isLoading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    'Send reset code'
                  )}
                </button>

                <Link
                  to="/login"
                  className="w-full px-5 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            </form>
          </div>
        )}

        {step === 'confirm' && (
          <div className="card p-8">
            <form className="space-y-6" onSubmit={handleConfirmReset}>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-text mb-2">
                  Verification code
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  required
                  className="input-field text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                />
                <p className="mt-2 text-xs text-text-muted text-center">
                  Enter the 6-digit code sent to {email}
                </p>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-text mb-2 flex items-center gap-2">
                  <Lock className="h-4 w-4 text-accent" />
                  New password
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-field pr-10"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-2 text-xs text-text-muted">
                  At least 8 characters with uppercase, lowercase, and numbers
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text mb-2">
                  Confirm new password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className="input-field"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {newPassword !== confirmPassword && confirmPassword && (
                  <p className="mt-2 text-sm text-destructive">Passwords do not match</p>
                )}
              </div>

              <div className="space-y-3 pt-2">
                <button
                  type="submit"
                  disabled={isLoading || newPassword !== confirmPassword}
                  className="w-full px-5 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent/20"
                >
                  {isLoading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    'Reset password'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('request')}
                  className="w-full px-5 py-3 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'success' && (
          <div className="card p-8">
            <div className="text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-text mb-2">
                  Password Reset Successful
                </h3>
                <p className="text-sm text-text-muted">
                  You can now sign in with your new password
                </p>
              </div>

              <Link
                to="/login"
                className="w-full px-5 py-3 bg-accent hover:bg-accent/90 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-accent/20"
              >
                Sign in with new password
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
